import type {
    TAddGroup,
    TUpdateGroup,
    TDeleteGroup,
    TValidateOrgGroupsWorkerPayload,
    TValidateOrgGroupsWorkerResult,
} from '@/store/group/types'
import type { Teach } from '@pocketprep/types'
import { runCloudFunction } from '@/store/ParseUtils'
import { fetchLibLoadable, resetLoadable } from '@/loadingUtils'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { instructorModule } from '@/store/instructor/module'
import { licenseModule } from '@/store/license/module'
import { statsModule } from '@/store/stats/module'
import { getInitialExamState } from '@/store/exam/state'
import { studentModule } from '@/store/student/module'
import ValidateOrgGroupsWorker from '@/store/group/workers/validateOrgGroupsWorker?worker'

const fetchGroupsActiveStudentIds = async () => {
    const currentOrganizationPromise = organizationModule.actions.fetchCurrentOrganization()
    const instructorsPromise = instructorModule.actions.fetchCurrentOrgInstructors()
    const licensesPromise = licenseModule.actions.fetchCurrentOrgLicenses()
    const examsPromise = examModule.actions.fetchCurrentOrgExams()
    const groupsPromise = fetchCurrentOrgGroups()

    await Promise.all([
        currentOrganizationPromise,
        instructorsPromise,
        licensesPromise,
        examsPromise,
        groupsPromise,
    ])
}

const deleteCompositeGroup = async (params: {
    organizationId: string
    groupCompositeKey: string
}) => {
    /**
     * Pass in a GroupCompositeKey, which is comprised of examGuid/owner/groupName
     * In a GroupCompositeKey, "owner" is either the instructor or 'system'
     **/
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('deleteGroup: Could not find currentOrganization')
    }

    const { organizationId, groupCompositeKey } = params

    // A GroupCompositeKey can point to multiple groups (across versions). We have to ensure that we delete them all
    const compositeGroupsForKey = groupModule.getters.getGroupsByGroupCompositeKey()[groupCompositeKey]

    // For each of the groups attached to the compositeKey, queue up a delete
    const deleteGroupsPromises = compositeGroupsForKey?.map(compositeGroup => {
        return runCloudFunction<TDeleteGroup>('deleteGroup', {
            organizationId,
            groupId: compositeGroup.objectId,
        })
    })

    if (deleteGroupsPromises) {
        await Promise.all(deleteGroupsPromises)
    }

    // Reset loadables
    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
    ])
}

// Potentially need to account for a composite group where this is called
const updateCompositeGroup = async (params: {
    groupCompositeKey: string
    updateParams: {
        name: string
        type: 'public' | 'private'
        orgStudentIds: string[]
        isPubliclyEditable: boolean
    }
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('updateGroup: Could not find currentOrganization')
    }

    const { groupCompositeKey, updateParams } = params

    const compositeGroupsForKey = groupModule.getters.getGroupsByGroupCompositeKey()[groupCompositeKey]

    const updateGroupsPromises = compositeGroupsForKey?.map(compositeGroup => {
        return runCloudFunction<TUpdateGroup>('updateGroup', {
            groupId: compositeGroup.objectId,
            name: updateParams.name,
            type: updateParams.type,
            orgStudentIds: updateParams.orgStudentIds,
            isPubliclyEditable: updateParams.isPubliclyEditable,
        })
    })

    if (updateGroupsPromises) {
        await Promise.all(updateGroupsPromises)
    }

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])
    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
    ])

    // clear group stats state so that it gets re-fetched when group is loaded again
    compositeGroupsForKey?.forEach(compositeGroup => {
        resetLoadable(statsModule.state.groupAggStats[compositeGroup.objectId])
    })

    // if current group is being updated then we want to force fetch stats in case number of students changed
    const currentGroupId = groupModule.state.currentGroupId
    const isUpdatedGroupCurrentGroup = !!compositeGroupsForKey?.find(compositeGroup =>
        compositeGroup.objectId === currentGroupId)
    if (isUpdatedGroupCurrentGroup) {
        await statsModule.actions.fetchCurrentGroupAggStats()
    }
}

const addGroup = async (params: {
    ownerId?: string
    organizationId: string
    orgExamId: string
    name: string
    type: 'public' | 'private' | 'system'
    orgStudentIds: string[]
    isPubliclyEditable: boolean
}) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        throw new Error('addGroup: Could not find currentOrganization')
    }

    const createdGroup = await runCloudFunction<TAddGroup>('addGroup', params)

    resetLoadable(groupModule.state.currentOrgGroups[currentOrganization.objectId])

    resetLoadable(instructorModule.state.currentOrgInstructors[currentOrganization.objectId])

    await Promise.all([
        fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructors(),
    ])

    return createdGroup
}

const fetchCurrentOrgGroups = async (forceFetch?: boolean) => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        return {}
    }

    return fetchLibLoadable(
        groupModule,
        'currentOrgGroups',
        currentOrganization.objectId,
        async () => {
            type TFetchGroups = (params: { organizationId: string }) => Promise<Teach.Class.OrgGroupJSON[]>
            const groups = await runCloudFunction<TFetchGroups>(
                'fetchGroups',
                { organizationId: currentOrganization.objectId }
            )
            return groups.reduce(
                (acc, group) => ({ ...acc, [group.objectId]: group }),
                {} as { [key: string]: Teach.Class.OrgGroupJSON }
            )
        },
        forceFetch)
}

const fetchCurrentVisibleOrgGroups = async () => {
    const groupsDispatch = fetchCurrentOrgGroups()
    const currentInstructorDispatch = instructorModule.actions.fetchCurrentOrgInstructor()

    await Promise.all([ groupsDispatch, currentInstructorDispatch ])

    return groupModule.getters.getCurrentVisibleOrgGroups()
}

const fetchCurrentGroup = async () => {
    const visibleGroups = await fetchCurrentVisibleOrgGroups()
    const currentGroupId = groupModule.state.currentGroupId || visibleGroups[0]?.objectId

    if (currentGroupId && !groupModule.state.currentGroupId) {
        groupModule.state.currentGroupId = currentGroupId
    }

    return groupModule.getters.getCurrentGroup()
}

const updateCurrentGroupId = async (groupId: string | null) => {
    if (groupModule.state.currentGroupId !== groupId) {

        // Grab previousExam for comparison to exam we are swapping to
        const previousExam = examModule.getters.getCurrentGroupExam()

        // Update group
        groupModule.state.currentGroupId = groupId

        const allExamMetadata = examModule.getters.getExamMetadata()
        const currentExam = examModule.getters.getCurrentGroupExam()
        const examMetadata = currentExam?.examMetadata.objectId && allExamMetadata[currentExam.examMetadata.objectId]

        const currentOrganization = organizationModule.getters.getCurrentOrganization()

        // Only fire if we are changing exams
        if (examMetadata && currentOrganization && previousExam?.objectId !== currentExam?.objectId) {
            // Reinitialize exam state
            examModule.state.currentExamQuestions = getInitialExamState().currentExamQuestions

            // set lastFetched to null
            resetLoadable(examModule.state.currentExamQuestions)

            await examModule.actions.fetchQuestionsByExam({
                compositeKey: examMetadata.compositeKey,
                organizationId: currentOrganization.objectId,
                examMetadataId: examMetadata.objectId,
            })
        }
    }

    await Promise.all([
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])
}

const validateOrgGroups = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()
    const organizationId = currentOrganization?.objectId
    if (!organizationId) {
        return
    }

    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        fetchCurrentOrgGroups(),
        studentModule.actions.fetchCurrentOrgStudents(),
    ])
    const compositeGroups = groupModule.getters.getCompositeGroups()
    const guidOrgExamLib = examModule.getters.getGuidOrgExamLib()
    const orgStudents = studentModule.getters.getCurrentOrgStudents()

    const workerPayload: TValidateOrgGroupsWorkerPayload = JSON.parse(JSON.stringify({
        compositeGroups,
        guidOrgExamLib,
        orgStudents,
        organizationId,
    }))
    const validateOrgGroupsWorker = new ValidateOrgGroupsWorker()
    validateOrgGroupsWorker.postMessage(workerPayload)
    validateOrgGroupsWorker.onmessage = async (event: MessageEvent<TValidateOrgGroupsWorkerResult>) => {
        const { payloadsForGroupsToAdd } = event.data
        // Create all the groups that need to be created
        await Promise.all(payloadsForGroupsToAdd.map(payload => groupModule.actions.addGroup(payload)))
    }
}

export default {
    fetchGroupsActiveStudentIds,
    addGroup,
    fetchCurrentOrgGroups,
    fetchCurrentVisibleOrgGroups,
    fetchCurrentGroup,
    updateCurrentGroupId,
    validateOrgGroups,
    deleteCompositeGroup,
    updateCompositeGroup,
}
